import { useCallback, useEffect, useState } from "react";
import { getBandDeletionImpact, type BandDeletionImpact } from "../../infra/api.js";
import { Button } from "../../ui/Button.js";

interface Props {
  bandId: string;
  bandName: string;
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

/**
 * Spec §F8 blocking delete confirmation. Fetches the impact (song count,
 * disk MB) from the backend on open, then locks the user into either
 * cancelling (default focus) or confirming. The actual `DELETE /api/bands/:id`
 * call is owned by the caller via `onConfirm`.
 *
 * Accessibility: focus traps Cancel by default, ESC cancels, click-outside
 * cancels — same as common production modals.
 */
export function DeleteBandModal({
  bandId,
  bandName,
  open,
  onCancel,
  onConfirm,
}: Props): JSX.Element | null {
  const [impact, setImpact] = useState<BandDeletionImpact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const cancelRefCallback = useCallback((node: HTMLButtonElement | null) => {
    // Focus the cancel button when it mounts so destructive action is never default.
    if (node) node.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    setImpact(null);
    setError(null);
    setConfirming(false);
    const ctl = new AbortController();
    void (async () => {
      try {
        const i = await getBandDeletionImpact(bandId, ctl.signal);
        if (!ctl.signal.aborted) setImpact(i);
      } catch (e) {
        if (ctl.signal.aborted) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => ctl.abort();
  }, [bandId, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  async function onConfirmClick(): Promise<void> {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-band-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-asphalt/80 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-md border border-vinyl bg-tape p-6 shadow-sleeve">
        <p className="catalog-num mb-3 text-vinyl">DELETE / IRREVERSÍVEL</p>
        <h2
          id="delete-band-title"
          className="font-display text-3xl leading-tight text-paper"
          style={{ fontVariationSettings: '"opsz" 60, "wght" 600, "SOFT" 80' }}
        >
          Apagar banda “{bandName}”?
        </h2>

        <p className="mt-3 text-sm leading-relaxed text-ink-muted">
          Esta ação remove a banda <strong>e</strong> todas as faixas dela, junto com os arquivos de
          áudio em disco. Os artistas continuam intactos.
        </p>

        {error && (
          <p
            role="alert"
            className="mt-4 border border-vinyl/40 bg-vinyl/5 p-3 font-mono text-xs uppercase tracking-widest2 text-vinyl"
          >
            erro: {error}
          </p>
        )}

        <dl
          aria-label="impacto"
          className="mt-5 grid grid-cols-2 gap-y-2 border border-border bg-tapeElev p-4 font-mono text-[11px] uppercase tracking-widest2"
        >
          <dt className="text-ink-muted">faixas</dt>
          <dd className="text-right text-paper tabular-nums" data-testid="impact-songs">
            {impact ? impact.song_count : "—"}
          </dd>
          <dt className="text-ink-muted">disco</dt>
          <dd className="text-right text-paper tabular-nums" data-testid="impact-mb">
            {impact ? `${impact.mb_on_disk.toFixed(2)} MB` : "—"}
          </dd>
        </dl>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            ref={cancelRefCallback}
            type="button"
            className="inline-flex items-center justify-center border border-border bg-transparent px-4 py-2 font-mono text-[11px] uppercase tracking-widest2 text-paper transition-colors hover:border-paper hover:bg-tapeElev disabled:opacity-40"
            onClick={onCancel}
            disabled={confirming}
          >
            Cancelar
          </button>
          <Button
            variant="danger"
            onClick={() => void onConfirmClick()}
            disabled={confirming || impact === null}
          >
            {confirming ? "apagando…" : "Confirmar delete"}
          </Button>
        </div>
      </div>
    </div>
  );
}
